import type { AxiosResponse } from 'axios'
import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router'

import type { CooperantDeliveryStatusesResponse } from '@/api/modules/cooperantDeliveryStatusesQueries'
import { hasPermission } from '@/composables/usePermissionHandler'
import { COOPERANT_ORDERS_PERMISSIONS, COOPERANT_STOCKS_PERMISSIONS } from '@/constants'
import { useCooperationJobsStore } from '@/stores'
import { useCooperantOrdersStore } from '@/stores/orders/cooperantOrders'
import type { CooperantOrderLookup } from '@/types/domain/orders/cooperant'
import { createPrefetchGuard } from '@/utils/routerGuards'
import CooperationJobs from '@/views/CooperationJobs/Index.vue'

type CooperantOrdersPrefetchStore = {
    fetchCooperantDeliveryStatusNames: () => Promise<AxiosResponse<CooperantDeliveryStatusesResponse>>
    fetchOrderLookupsAndFirstOrder: (orderNumbers: string[]) => Promise<AxiosResponse<CooperantOrderLookup[]>>
}

const routes: RouteRecordRaw[] = [
    {
        path: '/cooperation-jobs',
        name: 'cooperationJobs',
        component: CooperationJobs,
        redirect: { name: 'cooperationJobsList' },
        meta: {
            title: 'cooperationJobs.pageTitle',
            menuRendered: true,
            menuTitle: 'cooperationJobs.menuTitle',
            requiresAuth: true,
            permission: 'cooperation_jobs',
            icon: 'mdi-invoice-send-outline'
        },
        children: [
            {
                path: '',
                name: 'cooperationJobsList',
                component: () => import('@/views/CooperationJobs/CooperationJobs/Index.vue'),
                meta: {
                    title: 'cooperationJobs.pageTitle',
                    requiresAuth: true,
                    permission: 'cooperation_jobs_read'
                }
            },
            {
                path: 'stocks',
                name: 'cooperationJobsStocks',
                component: () => import('@/views/CooperationJobs/CooperantStocks/Index.vue'),
                meta: {
                    title: 'cooperationJobs.stocks.pageTitle',
                    requiresAuth: true,
                    permission: COOPERANT_STOCKS_PERMISSIONS.READ
                }
            },
            {
                path: ':id',
                name: 'cooperationJobDetails',
                component: () => import('@/views/CooperationJobs/CooperationJob/Index.vue'),
                beforeEnter: createPrefetchGuard({
                    getId: (to: RouteLocationNormalized) => to.params?.id,
                    fetch: async (id: string) => {
                        const cooperationJobsStore = useCooperationJobsStore()
                        const requests: Array<Promise<unknown>> = [cooperationJobsStore.fetchCooperationJob(id)]

                        if (!cooperationJobsStore.statuses.length) {
                            requests.push(cooperationJobsStore.fetchCooperationJobStatuses())
                        }

                        await Promise.all(requests)

                        if (cooperationJobsStore.currentJob?.canReportMaterialConsumptions) {
                            await cooperationJobsStore.fetchConsumptionMaterialTypes().catch(() => {
                                cooperationJobsStore.consumptionMaterialTypes = []
                            })
                        } else {
                            cooperationJobsStore.consumptionMaterialTypes = []
                        }

                        if (!hasPermission(COOPERANT_ORDERS_PERMISSIONS.READ)) return

                        const cooperantOrdersStore = useCooperantOrdersStore() as CooperantOrdersPrefetchStore
                        const orderNumbers = cooperationJobsStore.currentJob?.orderNumbers ?? []
                        if (!orderNumbers.length) return

                        const deliveryStatusesRequest = cooperantOrdersStore.fetchCooperantDeliveryStatusNames().catch(() => null)
                        try {
                            await cooperantOrdersStore.fetchOrderLookupsAndFirstOrder(orderNumbers)
                        } catch {
                            await deliveryStatusesRequest
                            return
                        }

                        await deliveryStatusesRequest
                    },
                    onMissingId: () => ({ name: 'cooperationJobsList' }),
                    onError: () => ({ name: 'cooperationJobsList' })
                }),
                meta: {
                    title: 'cooperationJobs.details.pageTitle',
                    requiresAuth: true,
                    permission: 'cooperation_jobs_read'
                }
            }
        ]
    }
]

export default routes
